import {
  getProductRiskFields,
  type RiskFieldDescriptor,
} from '@/app/config/products.config';
import { useCatalogLabels } from '@/features/quotes/hooks/common/use-catalog-labels.hook';
import type { ProposalRisk } from '@/services/proposals/types';
import type { CatalogId } from '@/shared/utils/catalog-label.util';
import { useMemo } from 'react';

/** Um campo de "Dados do Risco" pronto para renderizar. */
export type ResolvedRiskField = {
  label: string;
  value: string;
  span?: 1 | 2 | 3;
};

function resolveField(
  descriptor: RiskFieldDescriptor,
  risk: ProposalRisk,
  resolve: ReturnType<typeof useCatalogLabels>['resolve'],
  resolveAll: ReturnType<typeof useCatalogLabels>['resolveAll']
): string {
  const fallback = descriptor.value?.(risk);

  if (!descriptor.catalog || !descriptor.code) return fallback ?? '';

  const code = descriptor.code(risk);
  if (Array.isArray(code)) return resolveAll(descriptor.catalog, code);

  return resolve(
    descriptor.catalog,
    code as string | number | null | undefined,
    fallback
  );
}

/**
 * Traduz os campos de "Dados do Risco" declarados no registry de produtos para
 * os rótulos que o card de locais exibe.
 *
 * É aqui que mora a regra: qual campo do risco alimenta cada rótulo, qual
 * catálogo o traduz e qual a precedência entre a descrição vinda do BFF e a do
 * catálogo. O componente recebe `label`/`value` prontos e não conhece produto,
 * catálogo nem código.
 *
 * Deve ser chamado **uma vez, no componente pai**: os catálogos são carregados
 * e indexados aqui, não uma vez por local de risco.
 */
export function useResolvedRiskFields(
  productCode: string | undefined,
  risks: readonly ProposalRisk[]
): Map<number, ResolvedRiskField[]> {
  const descriptors = useMemo(
    () => getProductRiskFields(productCode),
    [productCode]
  );

  const catalogIds = useMemo(
    () =>
      Array.from(
        new Set(
          descriptors
            .map(descriptor => descriptor.catalog)
            .filter((catalog): catalog is CatalogId => Boolean(catalog))
        )
      ),
    [descriptors]
  );

  const { resolve, resolveAll } = useCatalogLabels(catalogIds);

  return useMemo(() => {
    const byRiskPk = new Map<number, ResolvedRiskField[]>();

    for (const risk of risks) {
      byRiskPk.set(
        risk.pk,
        descriptors.map(descriptor => ({
          label: descriptor.label,
          span: descriptor.span,
          value: resolveField(descriptor, risk, resolve, resolveAll),
        }))
      );
    }

    return byRiskPk;
  }, [risks, descriptors, resolve, resolveAll]);
}
